// @ts-nocheck
import { isUndefined } from "lodash";
import { getBreaks, getBreaksEstimate } from "./calcUtils";

/**
 * if isPrefill then send undefined if it's undefine else return the original value
 */
const getUndefineOrOriginalValue = ({ value, isPrefill }) => {
  return isPrefill ? value : value || "0";
};

/**
 * convert position terms to global terms term
 */
export const convertPositionTerms = ({ position }) => ({
  halfDay: !isUndefined(position?.positionTerms?.halfDayHours)
    ? position?.positionTerms?.halfDayHours
    : 0,
  fullDay: !isUndefined(position?.positionTerms?.fullDayHours)
    ? position?.positionTerms?.fullDayHours
    : 0,
  overTime: !isUndefined(position?.positionTerms?.overTimeHours)
    ? position?.positionTerms?.overTimeHours
    : 0,
  contractorHourlyRate: !isUndefined(
    position?.positionTerms?.contractorHourlyRate,
  )
    ? position?.positionTerms?.contractorHourlyRate
    : false,
  clientHourlyRate: !isUndefined(position?.positionTerms?.clientHourlyRate)
    ? position?.positionTerms?.clientHourlyRate
    : false,
});

/**
 * get final terms for position
 */
export const getFinalTerms = ({ position, eventTerms, isClient = true }) => {
  if (
    (isClient && position?.positionTerms?.termsForClient) ||
    (!isClient && position?.positionTerms?.termsForContractor)
  ) {
    return {
      halfDay: !isUndefined(position?.positionTerms?.halfDayHours)
        ? position?.positionTerms?.halfDayHours
        : eventTerms?.halfDayHours || eventTerms?.halfDay,
      fullDay: !isUndefined(position?.positionTerms?.fullDayHours)
        ? position?.positionTerms?.fullDayHours
        : eventTerms?.fullDayHours || eventTerms?.fullDay,
      overTime: !isUndefined(position?.positionTerms?.overTimeHours)
        ? position?.positionTerms?.overTimeHours
        : eventTerms?.overTimeHours || eventTerms?.overTime,
      contractorHourlyRate: !isUndefined(
        position?.positionTerms?.contractorHourlyRate,
      )
        ? position?.positionTerms?.contractorHourlyRate
        : eventTerms?.contractorHourlyRate,
      clientHourlyRate: !isUndefined(position?.positionTerms?.clientHourlyRate)
        ? position?.positionTerms?.clientHourlyRate
        : eventTerms?.clientHourlyRate,
      clientHalfDayInc: !isUndefined(position?.positionTerms?.clientHalfDayInc)
        ? position?.positionTerms?.clientHalfDayInc
        : eventTerms?.clientHalfDayInc,
      contractorHalfDayInc: !isUndefined(
        position?.positionTerms?.contractorHalfDayInc,
      )
        ? position?.positionTerms?.contractorHalfDayInc
        : eventTerms?.contractorHalfDayInc,
      californiaContractorPO_SOW: eventTerms?.californiaContractorPO_SOW,
    };
  } else {
    return {
      halfDay: eventTerms?.halfDayHours || eventTerms?.halfDay,
      fullDay: eventTerms?.fullDayHours || eventTerms?.fullDay,
      overTime: eventTerms?.overTimeHours || eventTerms?.overTime,
      contractorHourlyRate: eventTerms?.contractorHourlyRate,
      clientHourlyRate: eventTerms?.clientHourlyRate,
      clientHalfDayInc: eventTerms?.clientHalfDayInc,
      contractorHalfDayInc: eventTerms?.contractorHalfDayInc,
      californiaContractorPO_SOW: eventTerms?.californiaContractorPO_SOW,
    };
  }
};

/**
 * combine event and position terms for position
 */
export const combinePosAndEventTerms = ({ position, eventTerms }) => {
  return {
    halfDay: !isUndefined(position?.positionTerms?.halfDayHours)
      ? position?.positionTerms?.halfDayHours
      : eventTerms?.halfDayHours || eventTerms?.halfDay,
    fullDay: !isUndefined(position?.positionTerms?.fullDayHours)
      ? position?.positionTerms?.fullDayHours
      : eventTerms?.fullDayHours || eventTerms?.fullDay,
    overTime: !isUndefined(position?.positionTerms?.overTimeHours)
      ? position?.positionTerms?.overTimeHours
      : eventTerms?.overTimeHours || eventTerms?.overTime,
    contractorHourlyRate: !isUndefined(
      position?.positionTerms?.contractorHourlyRate,
    )
      ? position?.positionTerms?.contractorHourlyRate
      : eventTerms?.contractorHourlyRate,
    clientHourlyRate: !isUndefined(position?.positionTerms?.clientHourlyRate)
      ? position?.positionTerms?.clientHourlyRate
      : eventTerms?.clientHourlyRate,
    clientHalfDayInc: !isUndefined(position?.positionTerms?.clientHalfDayInc)
      ? position?.positionTerms?.clientHalfDayInc
      : eventTerms?.clientHalfDayInc,
    contractorHalfDayInc: !isUndefined(
      position?.positionTerms?.contractorHalfDayInc,
    )
      ? position?.positionTerms?.contractorHalfDayInc
      : eventTerms?.contractorHalfDayInc,
  };
};

/**
 * if a position is of default full day then it'll update the terms for them
 * @param (Object) options - The options for getting terms
 *
 * @returns {number | string} The value which will be needed
 */
export function getHalfDayWithDefaultCheck({
  terms,
  halfDay,
  hourlyRate,
  pos,
}) {
  if (
    !halfDay &&
    !hourlyRate &&
    (pos?.positionData?.isFullDay || pos?.isFullDay)
  ) {
    return { ...terms, halfDay: 0 };
  }

  return terms;
}

/**
 * get object for estimate and sow calculation
 * @param {Object} options - The options for estimate calculation.
 * @param {string} options.timezone - The timezone of the event.
 * @param {Object} options.pos - The position object, typically including associated terms.
 * @param {boolean} options.addHalfDay - Whether to include a half-day in the calculation.
 * @param {Object} options.terms - The terms of the event (e.g., dates, duration).
 * @param {number|string} options.appliedRate - The applied rate for the position (hourly or flat).
 * @param {boolean} options.hourlyRate - Whether to calculate based on an hourly rate.
 * @param {boolean} options.isClient - Whether the calculation is for a client or false for contractor.
 *
 * @returns {Object} The object containing calculation data for the estimate.
 */
export const getObjForEstimateCalculation = ({
  timezone,
  pos,
  addHalfDay = true,
  terms,
  appliedRate,
  isClient = true,
}) => {
  const californaTerms = {
    halfDay: 5,
    fullDay: 8,
    overTime: 4,
  };

  let newTerms = getFinalTerms({ eventTerms: terms, position: pos, isClient });

  if (!isClient && terms?.californiaContractorPO_SOW) {
    newTerms = { ...newTerms, ...californaTerms };
  }

  const halfDayKey = isClient ? "clientHalfDayInc" : "contractorHalfDayInc";
  const hourlyRateKey = isClient ? "clientHourlyRate" : "contractorHourlyRate";

  const halfDayResult =
    addHalfDay && pos?.isHalfdayHours
      ? isUndefined(pos?.positionTerms?.[halfDayKey])
        ? terms?.[halfDayKey]
        : pos?.positionTerms?.[halfDayKey]
      : false;

  const hourlyRateResult = isUndefined(pos?.positionTerms?.[hourlyRateKey])
    ? terms?.[hourlyRateKey]
    : pos?.positionTerms?.[hourlyRateKey];

  return {
    terms: getHalfDayWithDefaultCheck({
      terms: newTerms,
      halfDay: halfDayResult,
      hourlyRate: hourlyRateResult,
      pos,
    }),
    appliedRate,
    data: {
      start: pos?.timeIn,
      end: pos?.timeOut,
      timezone,
      numberOfHours: pos?.numberOfHours,
      travelDays: pos?.traveldayId,
      fullDay: newTerms?.fullDay,
      halfDay: newTerms?.halfDay,
      isClient,
    },
    breaks: getBreaksEstimate(pos, true),
    isHalfDay: halfDayResult,
    isHourlyRate: hourlyRateResult,
  };
};

/**
 * function get get positoin terms of all positions
 */
export const getAllPositionTerms = ({ positions }) => {
  let positionObj = {};
  Object.keys(positions)?.forEach((ses) => {
    positions?.[ses]?.forEach((pos) => {
      if (isUndefined(pos?.positionTerms)) positionObj[pos?._id] = null;
      else positionObj[pos?._id] = pos?.positionTerms;
    });
  });
};

/**
 * get object for PO calculation
 * @param {Object} options - The options for estimate calculation.
 * @param {string} options.timezone - The timezone of the event.
 * @param {Object} options.pos - The position object, typically including associated terms.
 * @param {boolean} options.addHalfDay - Whether to include a half-day in the calculation.
 * @param {Object} options.terms - The terms of the event (e.g., dates, duration).
 * @param {number|string} options.appliedRate - The applied rate for the position (hourly or flat).
 * @param {boolean} options.hourlyRate - Whether to calculate based on an hourly rate.
 * @param {boolean} options.isClient - Whether the calculation is for a client.
 * @param {boolean} options.isContractorDiff - Whether the calculation is for a client.
 * @param {boolean} options.disableGivenOTDT - Whether to use the given ot and dt and mot.
 *
 * @returns {Object} The object containing calculation data for the estimate.
 */
export const getObjForPOCalculation = ({
  timezone,
  pos,
  addHalfDay = true,
  terms,
  appliedRate,
  isClient = true,
  isContractorDiff = false,
  disableGivenOTDT = false,
  isPrefill = false,
}) => {
  const californaTerms = {
    halfDay: 5,
    fullDay: 8,
    overTime: 4,
  };
  let newTerms = getFinalTerms({ eventTerms: terms, position: pos, isClient });

  if (!isClient && terms?.californiaContractorPO_SOW) {
    newTerms = { ...newTerms, ...californaTerms };
  }

  const halfDayKey = isClient ? "clientHalfDayInc" : "contractorHalfDayInc";
  const hourlyRateKey = isClient ? "clientHourlyRate" : "contractorHourlyRate";

  const halfDayResult =
    addHalfDay && pos?.isHalfdayHours
      ? isUndefined(pos?.positionTerms?.[halfDayKey])
        ? terms?.[halfDayKey]
        : pos?.positionTerms?.[halfDayKey]
      : false;

  const hourlyRateResult = isUndefined(pos?.positionTerms?.[hourlyRateKey])
    ? terms?.[hourlyRateKey]
    : pos?.positionTerms?.[hourlyRateKey];

  return {
    terms: getHalfDayWithDefaultCheck({
      terms: newTerms,
      halfDay: halfDayResult,
      hourlyRate: hourlyRateResult,
      pos,
    }),
    appliedRate,
    data: {
      start: isContractorDiff
        ? pos?.contractorActualStart || pos?.timeIn
        : pos?.actualStart || pos?.timeIn,
      end: isContractorDiff
        ? pos?.contractorActualEnd || pos?.timeOut
        : pos?.actualEnd || pos?.timeOut,
      timezone,
      numberOfHours: pos?.numberOfHours,
      travelDays: pos?.traveldayId,
      fullDay: newTerms?.fullDay,
      halfDay: newTerms?.halfDay,
      isClient,
    },
    breaks: getBreaks(pos, false, isContractorDiff && !isClient, !isClient),
    isHalfDay: halfDayResult,
    isHourlyRate: hourlyRateResult,
    givenOtAndDt: disableGivenOTDT
      ? {}
      : {
          givenOt: getUndefineOrOriginalValue({
            value: isContractorDiff ? pos?.contractorOT : pos?.oT,
            isPrefill,
          }),
          givenDt: getUndefineOrOriginalValue({
            value: isContractorDiff ? pos?.contractorDT : pos?.dT,
            isPrefill,
          }),
          givenMOt: getUndefineOrOriginalValue({
            value: isContractorDiff
              ? pos?.contractorActualMidnightOT
              : pos?.actualMidnightOT,
            isPrefill,
          }),
        },
    isFullDayPos:
      (pos?.positionData?.isFullDay || pos?.isFullDay) &&
      !halfDayResult &&
      !hourlyRateResult,
  };
};

/**
 * get object for Invoice calculation
 * @param {Object} options - The options for estimate calculation.
 * @param {string} options.timezone - The timezone of the event.
 * @param {Object} options.pos - The position object, typically including associated terms.
 * @param {boolean} options.addHalfDay - Whether to include a half-day in the calculation.
 * @param {Object} options.terms - The terms of the event (e.g., dates, duration).
 * @param {number|string} options.appliedRate - The applied rate for the position (hourly or flat).
 * @param {boolean} options.hourlyRate - Whether to calculate based on an hourly rate.
 * @param {boolean} options.isClient - Whether the calculation is for a client.
 *
 * @returns {Object} The object containing calculation data for the estimate.
 */
export const getObjForInvoiceCalculation = ({
  timezone,
  pos,
  addHalfDay = true,
  terms,
  appliedRate,
  isClient = true,
}) => {
  let newTerms = getFinalTerms({ eventTerms: terms, position: pos, isClient });

  const halfDayKey = isClient ? "clientHalfDayInc" : "contractorHalfDayInc";
  const hourlyRateKey = isClient ? "clientHourlyRate" : "contractorHourlyRate";

  const halfDayResult =
    addHalfDay && pos?.isHalfdayHours
      ? isUndefined(pos?.positionTerms?.[halfDayKey])
        ? terms?.[halfDayKey]
        : pos?.positionTerms?.[halfDayKey]
      : false;

  const hourlyRateResult = isUndefined(pos?.positionTerms?.[hourlyRateKey])
    ? terms?.[hourlyRateKey]
    : pos?.positionTerms?.[hourlyRateKey];

  return {
    terms: getHalfDayWithDefaultCheck({
      terms: newTerms,
      halfDay: halfDayResult,
      hourlyRate:
        hourlyRateResult ||
        (halfDayResult ? pos?.isHalfDayManualOverride : false),
      pos,
    }),
    appliedRate,
    data: {
      start: pos?.actualStart || pos?.timeIn,
      end: pos?.actualEnd || pos?.timeOut,
      timezone,
      numberOfHours: pos?.numberOfHours,
      travelDays: pos?.traveldayId,
      fullDay: newTerms?.fullDay,
      halfDay: newTerms?.halfDay,
      isClient,
    },
    breaks: getBreaks(pos),
    isHalfDay: halfDayResult,
    isHourlyRate: hourlyRateResult,
    isHDOverride: halfDayResult ? pos?.isHalfDayManualOverride : false,
    givenOtAndDt: {
      givenOt: pos?.oT || "0",
      givenDt: pos?.dT || "0",
      givenMOt: pos?.actualMidnightOT || "0",
    },
  };
};
